import { LoadingSpinner } from "components/LoadingSpinner";
import { adjustmentTypesMapping } from "constants/order";
import { centsToDollars, parsePrice } from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { AnyAction, Dispatch } from "redux";
import {
  IPriceBreakdownRows,
  toggleDisplayAdjustments,
} from "store/slices/serviceDetails";
import styled from "styled-components";

import {
  buildCptCodeWithRvu,
  buildSimpleFirstColumnTemplate,
} from "../../builders";
import { summariseBreakdown } from "../Common/breakdown";
import { BreakdownHeader } from "../Common/BreakdownHeader";
import { StyledBreakdownPanel } from "../Common/styled";

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
`;

const BypassedRulesContainer = styled.div`
  background-color: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin: 0.875rem 1.25rem;
`;

const BypassedRuleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0;
`;

interface IExcludedNcciInfo {
  rulesetId: string;
  cptCode: string;
  discountedCptCode: string;
}

const renderModifierBreakdown = (
  modifierBreakdown: Record<string, { total: number; paid: number }>
) => {
  return Object.entries(modifierBreakdown)
    .filter(([, data]) => data.total > 0)
    .map(([modifier, data]) => {
      const pct = Math.round((data.paid / data.total) * 100);
      const label =
        modifier === "none" ? "No modifier" : `Modifier ${modifier}`;
      return `${label}: ${pct}% paid (${data.paid}/${data.total})`;
    })
    .join(" | ");
};

function amountTemplate(row: { amount: number }) {
  return (
    <span className="font-semibold" style={{ color: "var(--brand-black)" }}>
      {parsePrice(centsToDollars(row.amount))}
    </span>
  );
}

const renderAdjustmentText = (row: any) => {
  return (
    <>
      {adjustmentTypesMapping[row.specialty] || row.specialty}
      {row.hierarchyOverridden && " (Overridden)"}
      {row.discountExplanation && (
        <>
          <br />
          <span style={{ color: "#666", fontSize: "0.9em" }}>
            {row.discountExplanation}
          </span>
        </>
      )}
      {row.paymentStats && row.paymentStats.totalClaims > 0 && (
        <>
          <br />
          <span style={{ color: "#1565c0", fontSize: "0.85em" }}>
            <i
              className="pi pi-chart-bar"
              style={{ fontSize: "0.8em", marginRight: "0.3rem" }}
            />
            {row.paymentStats.paidPercentage}% of claims for this payer were
            paid for this code ({row.paymentStats.paidClaims}/
            {row.paymentStats.totalClaims} claims)
          </span>
          {row.paymentStats.modifierBreakdown &&
            Object.keys(row.paymentStats.modifierBreakdown).length > 0 && (
              <>
                <br />
                <span
                  style={{
                    color: "#666",
                    fontSize: "0.8em",
                    marginLeft: "1.1rem",
                  }}
                >
                  {renderModifierBreakdown(row.paymentStats.modifierBreakdown)}
                </span>
              </>
            )}
        </>
      )}
    </>
  );
};

export function AdjustmentDetails({
  displayAdjustments,
  priceBreakdownRows,
  dispatch,
  priceExecuting,
  excludedNcciInfo = [],
  onNcciToggle,
}: {
  displayAdjustments: boolean;
  priceBreakdownRows: IPriceBreakdownRows;
  dispatch: Dispatch<AnyAction>;
  priceExecuting: boolean;
  excludedNcciInfo?: IExcludedNcciInfo[];
  onNcciToggle?: (
    rulesetId: string,
    info: { cptCode: string; discountedCptCode: string }
  ) => void;
}) {
  const isNcciBypassable = (row: any) =>
    row.specialty === "NCCI" && row.modifier != null && row.modifier !== 0;

  const hasNcciBypassToggle = priceBreakdownRows.adjustments.some((row: any) =>
    isNcciBypassable(row)
  );

  const renderActionColumn = (row: any) => {
    if (!isNcciBypassable(row) || !onNcciToggle) {
      return null;
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <InputSwitch
          checked
          onChange={() =>
            onNcciToggle(row.rulesetId, {
              cptCode: row.ncciPrimaryCptCode,
              discountedCptCode: row.cptCode,
            })
          }
          disabled={priceExecuting}
          tooltip="Bypass this NCCI adjustment"
          tooltipOptions={{ position: "left" }}
        />
      </div>
    );
  };

  const { count, totalInCents } = summariseBreakdown(
    priceBreakdownRows.adjustments
  );

  return (
    <StyledBreakdownPanel>
      <BreakdownHeader
        title="Adjustments"
        count={count}
        totalInCents={totalInCents}
        noun="adjustment"
        expanded={displayAdjustments}
        onToggle={() => dispatch(toggleDisplayAdjustments())}
      />

      {displayAdjustments &&
        (!priceExecuting ? (
          <div className="breakdown-body">
            <DataTable
              value={priceBreakdownRows.adjustments}
              rowGroupMode="rowspan"
              responsiveLayout="scroll"
              emptyMessage="No adjustments available."
            >
              <Column
                field="providerName"
                style={{ width: "20%" }}
                body={(row) => buildSimpleFirstColumnTemplate(row)}
              />
              <Column
                field="internalId"
                style={{ width: "15%", color: "var(--brand-gray-600)" }}
                body={(row) => buildCptCodeWithRvu(row)}
              />
              <Column
                field="amount"
                style={{ width: "15%" }}
                body={(row) => amountTemplate(row)}
              />
              <Column
                field="specialty"
                style={{ width: hasNcciBypassToggle ? "40%" : "50%" }}
                body={renderAdjustmentText}
              />
              {hasNcciBypassToggle && (
                <Column style={{ width: "10%" }} body={renderActionColumn} />
              )}
            </DataTable>

            {excludedNcciInfo.length > 0 && (
              <BypassedRulesContainer>
                <div
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#6d4c00",
                  }}
                >
                  <i
                    className="pi pi-info-circle"
                    style={{ marginRight: "0.4rem" }}
                  />
                  NCCI Adjustments Bypassed
                </div>
                {excludedNcciInfo.map((info) => (
                  <BypassedRuleRow key={info.rulesetId}>
                    <span style={{ fontSize: "0.85rem", color: "#5d4037" }}>
                      Codes {info.cptCode} and {info.discountedCptCode} — NCCI
                      bypass applied
                    </span>
                    <InputSwitch
                      checked={false}
                      onChange={() =>
                        onNcciToggle?.(info.rulesetId, {
                          cptCode: info.cptCode,
                          discountedCptCode: info.discountedCptCode,
                        })
                      }
                      disabled={priceExecuting}
                      tooltip="Re-enable this NCCI adjustment"
                      tooltipOptions={{ position: "left" }}
                    />
                  </BypassedRuleRow>
                ))}
              </BypassedRulesContainer>
            )}
          </div>
        ) : (
          <div className="breakdown-body">
            <LoadingContainer>
              <LoadingSpinner customStyle={{ width: "60px", height: "60px" }} />
              <h3>Calculating Adjustments</h3>
            </LoadingContainer>
          </div>
        ))}
    </StyledBreakdownPanel>
  );
}
